import commonSenseApi from '@/state/apis/commonSense'

import { objectToQueryString } from '@/utils/queryString'

import { IMPROVE_LEGAL_PRACTICE } from './applicableLaw/privacyIssue/types'

import {
  PostRequest,
  PostResponseBody,
  PatchRequest,
  PatchResponseBody,
  GetProductPrivacyIssueRequest,
  GetProductPrivacyIssueResponseBody,
  GetProductPrivacyIssueListRequest,
  GetProductPrivacyIssueListResponseBody,
  PutProductPrivacyIssueRequest,
  PutProductPrivacyIssueResponseBody,
  GetJurisdictionalQuestionsRequest,
  GetJurisdictionalQuestionsResponseBody,
  PutJurisdictionalQuestionsRequest,
  PutJurisdictionalQuestionsResponseBody,
  HIGH_PRIORITY_FIX,
  EASY_FIX,
  TOP_PRIORITY,
} from './types'

const productService = commonSenseApi.injectEndpoints({
  endpoints: build => ({
    postCreateProduct: build.mutation<PostResponseBody, PostRequest>({
      query: ({
        body, params: { id },
      }) => ({
        url: `v1/organizations/${id}/products`,
        method: 'POST',
        body,
      }),
    }),
    patchProduct: build.mutation<PatchResponseBody, PatchRequest>({
      query: ({
        body, params: {
          id, productId,
        },
      }) => ({
        url: `v1/organizations/${id}/products/${productId}`,
        method: 'PATCH',
        body,
      }),
    }),
    getProductPrivacyIssue: build.query<GetProductPrivacyIssueResponseBody, GetProductPrivacyIssueRequest>({
      query: ({
        params: {
          id,
          productId,
          privacyIssueId,
        },
      }) => ({
        url: `v1/organizations/${id}/products/${productId}/privacy-issues/${privacyIssueId}`,
        method: 'GET',
      }),
      providesTags: [ 'ProductPrivacyIssue' ],
    }),
    getJurisdictionalQuestions: build.query<GetJurisdictionalQuestionsResponseBody, GetJurisdictionalQuestionsRequest>({
      query: ({
        params: {
          id,
          productId,
        },
      }) => ({
        url: `v1/organizations/${id}/products/${productId}/jurisdictional-questions`,
        method: 'GET',
      }),
      providesTags: [ 'ProductPrivacyIssue' ],
    }),
    getProductPrivacyIssueList: build.query<GetProductPrivacyIssueListResponseBody, GetProductPrivacyIssueListRequest>({
      query: ({
        params: {
          id,
          productId,
        },
        query,
      }) => ({
        url: `v1/organizations/${id}/products/${productId}/privacy-issues?${objectToQueryString(query)}`,
        method: 'GET',
      }),
      serializeQueryArgs: ({ queryArgs }) => `PrivacyIssues::${queryArgs?.query?.category}`,
      // eslint-disable-next-line
      merge: (currentCache: any, responseData: any) => {
        const {
          total,
          offset,
          limit,
          items,
        } = responseData.data

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let newItems: any[]
        if (offset < currentCache.data?.items.length) {
          newItems = items
        }
        else {
          newItems = (currentCache.data?.items || []).concat(items)
        }

        currentCache.data = {
          total,
          offset,
          limit,
          items: newItems,
        }
      },
      // eslint-disable-next-line
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
    }),
    putProductPrivacyIssue: build.mutation<PutProductPrivacyIssueResponseBody, PutProductPrivacyIssueRequest>({
      query: ({
        params: {
          id, productId, privacyIssueId,
        },
        body,
      }) => ({
        url: `v1/organizations/${id}/products/${productId}/privacy-issues/${privacyIssueId}`,
        method: 'PUT',
        body,
      }),
      async onQueryStarted(arg, {
        dispatch, queryFulfilled,
      }) {
        try {
          const { data: response } = await queryFulfilled
          // Update the cache for the current question with the fresh data from the PUT response
          // This prevents a redundant GET request while keeping the cache fresh
          const previousQuestion = response?.data?.previousProductPrivacyIssue
          if (previousQuestion) {
            dispatch(
              productService.util.updateQueryData(
                'getProductPrivacyIssue',
                { params: arg.params },
                draft => {
                  // Replace the cached data with the updated question from the PUT response
                  draft.data = previousQuestion
                },
              ),
            )
          }
        }
        catch {
          // If mutation fails, no cache update needed
        }
      },
      invalidatesTags: [
        'ProductScore',
        'PrivacyIssues',
        'PrivacyIssueScore',
        'RecommendedAction',
        // The evaluation screen renders these same answers as `currentAnswer`.
        'ProductEvaluationAnswers',
        `PrivacyIssues::${HIGH_PRIORITY_FIX}`,
        `PrivacyIssues::${IMPROVE_LEGAL_PRACTICE}`,
        `PrivacyIssues::${EASY_FIX}`,
        `PrivacyIssues::${TOP_PRIORITY}`,
      ],
    }),
    putJurisdictionalQuestions: build.mutation<PutJurisdictionalQuestionsResponseBody, PutJurisdictionalQuestionsRequest>({
      query: ({
        params: {
          id, productId,
        },
        body,
      }) => ({
        url: `v1/organizations/${id}/products/${productId}/jurisdictional-questions`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [
        'PrivacyTopics',
        'ProductScore',
        'PrivacyIssues',
        'PrivacyIssueScore',
        // Keep 'ProductPrivacyIssue' here because backend doesn't return updated data
        // in the response, so we need to refetch to keep cache fresh
        'ProductPrivacyIssue',
        'RecommendedAction',
        // Jurisdictional answers change which questions apply, and the evaluation
        // screen only lists the ones that do.
        'ProductEvaluationAnswers',
        `PrivacyIssues::${HIGH_PRIORITY_FIX}`,
        `PrivacyIssues::${IMPROVE_LEGAL_PRACTICE}`,
        `PrivacyIssues::${EASY_FIX}`,
        `PrivacyIssues::${TOP_PRIORITY}`,
      ],
    }),
  }),
})

export default productService

export const {
  usePostCreateProductMutation,
  usePatchProductMutation,
  useGetProductPrivacyIssueQuery,
  useGetProductPrivacyIssueListQuery,
  usePutProductPrivacyIssueMutation,
  useGetJurisdictionalQuestionsQuery,
  usePutJurisdictionalQuestionsMutation,
} = productService

export const {
  endpoints: {
    postCreateProduct,
    patchProduct,
    getProductPrivacyIssue,
    getProductPrivacyIssueList,
    putProductPrivacyIssue,
  },
} = productService
